import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { createOrderFromPromo } from '@/lib/supabase/promos';
import type { NoteItem, PromoItem, NoteLocation } from '@/types/location';
import { ShoppingBasket as ShoppingCart, Minus, Plus, X, MapPin, Info, LocateFixed } from 'lucide-react';
import { Car, Motorbike, Bike, Truck } from '@/components/icons';
import { formatCurrency, calculateDistance } from '@/lib/utils';
import { getPriceConfig } from '@/config/deliveryPrices';
import { useSession } from '@/context/SessionContext';
import { COMMISSIONS } from '@/lib/constants/commissions';
import LocationAutocomplete from './LocationAutocomplete';
import { getUserAddresses, UserAddress } from '@/lib/supabase/userAddresses';
import { AnimatePresence, motion } from 'framer-motion';
import MapPinSelector from './MapPinSelector';
import { Textarea } from '@/components/ui/textarea';
import { QuantitySelector } from '@/components/QuantitySelector';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { useVibrate } from '@/hooks/useVibrate';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PromoBuyerActionsProps {
  note: NoteItem;
  userId: string | null | undefined;
  onOrderCreated?: (orderId: string) => void;
  transportationType?: string;
  onTransportationTypeChange?: (value: string) => void;
  hideTransportSelector?: boolean;
}

export default function PromoBuyerActions({
  note,
  userId,
  onOrderCreated,
  transportationType,
  onTransportationTypeChange,
  hideTransportSelector = false,
}: PromoBuyerActionsProps) {
  const { toast } = useToast();
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  const { relevantLocation, setRelevantLocation } = useSession();
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Record<number, number>>({}); // index -> quantity
  const [internalTransportationType, setInternalTransportationType] = useState<string>('car');
  const [isChangingAddress, setIsChangingAddress] = useState(false);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [isTypingLocation, setIsTypingLocation] = useState(false);
  const [calculatingLocation, setCalculatingLocation] = useState(false);
  const [showCostTooltip, setShowCostTooltip] = useState(false);
  
  // Estados para Refinar Dirección (igual que RequestRide)
  const [isRefining, setIsRefining] = useState(false);
  const [tempCoords, setTempCoords] = useState<{lat: number, lon: number} | null>(null);
  const [refineDetails, setRefineDetails] = useState({
    streetName: '',
    altura: '',
    floor: '',
    apartment: '',
    notes: ''
  });
  const [showAlturaError, setShowAlturaError] = useState(false);

  // Parse items safely (handle both JSON string and array)
  const parseItems = (items: any): PromoItem[] => {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
      try {
        const parsed = JSON.parse(items);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const items = parseItems(note.items) as PromoItem[];
  const DRAFT_KEY = `promo_draft_${note.id}_${userId}`;
  const effectiveTransportationType = transportationType ?? internalTransportationType;
  const setTransportationType = onTransportationTypeChange ?? setInternalTransportationType;

  // Cargar direcciones cuando el componente se monta o el usuario cambia
  React.useEffect(() => {
    if (userId) {
      getUserAddresses(userId).then(setAddresses).catch(() => setAddresses([]));
    }
  }, [userId]);

  // Cargar draft al montar
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          setSelectedItems(draft.selectedItems || {});
          setTransportationType(draft.transportationType || 'car');
          if (draft.refineDetails) {
            setRefineDetails(draft.refineDetails);
          }
        } catch (e) {
          console.error('[PromoBuyerActions] Error loading draft:', e);
        }
      }
    }
  }, [DRAFT_KEY, note.id, userId, setTransportationType]);

  // Guardar draft cada vez que cambien los datos
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const draft = {
        selectedItems,
        transportationType: effectiveTransportationType,
        refineDetails
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }
  }, [selectedItems, effectiveTransportationType, refineDetails, DRAFT_KEY]);

  const handleQuantityChange = useCallback((index: number, quantity: number) => {
    if (quantity > 0) {
      setSelectedItems(prev => ({
        ...prev,
        [index]: quantity,
      }));
    } else {
      setSelectedItems(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }
  }, []);

  const calculateTotal = useCallback(() => {
    return Object.entries(selectedItems).reduce((sum, [indexStr, quantity]) => {
      const index = parseInt(indexStr);
      if (items[index]) {
        return sum + (items[index].price * quantity);
      }
      return sum;
    }, 0);
  }, [selectedItems, items]);

  const handleConsiguemelo = useCallback(async () => {
    if (!userId) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para crear una orden',
        variant: 'destructive',
      });
      return;
    }

    // Validar que al menos un item fue seleccionado
    if (Object.keys(selectedItems).length === 0) {
      toast({
        title: 'Error',
        description: 'Selecciona al menos un producto',
        variant: 'destructive',
      });
      return;
    }

    // Validar transporte seleccionado
    if (!effectiveTransportationType) {
      toast({
        title: 'Error',
        description: 'Selecciona un tipo de transporte',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Construir array de items seleccionados
      const selectedItemsList = Object.entries(selectedItems).map(([indexStr, quantity]) => {
        const index = parseInt(indexStr);
        return {
          ...items[index],
          quantity,
        };
      });

      const totalAmount = finalTotal;


      // Llamar RPC para crear order y publicar en changas_board
      // Pasar subtotal, comisiones desglosadas, y total final
      const response = await createOrderFromPromo(
        note.id,
        userId,
        effectiveTransportationType,
        JSON.stringify(selectedItemsList),
        Number(deliveryFee.toFixed(2)),
        Number(deliveryDistance.toFixed(2)),
        Number(finalTotal.toFixed(2)),
        {
          pickup_latitude: note.location_latitude || note.latitude || 0,
          pickup_longitude: note.location_longitude || note.longitude || 0,
          pickup_address_name: note.location_display_name || note.address || 'Ubicación de origen',
          delivery_latitude: relevantLocation?.latitude || 0,
          delivery_longitude: relevantLocation?.longitude || 0,
          delivery_address_name: relevantLocation?.displayName || 'Ubicación de entrega'
        },
        Number(subtotal.toFixed(2)),           // subtotal
        Number(systemCommission.toFixed(2)),   // system_fee
        Number(hunterCommission.toFixed(2))    // hunter_fee
      );

      if (!response || !response.success) {
        console.error('[PromoBuyerActions] Error:', response?.error);
        toast({
          title: 'Error',
          description: response?.error || 'No se pudo crear la orden. Intenta nuevamente.',
          variant: 'destructive',
        });
        return;
      }


      toast({
        title: '¡Orden creada!',
        description: `Tu orden de $${totalAmount.toFixed(2)} fue publicada en Changas Board`,
      });

      // Reset form
      setSelectedItems({});
      setTransportationType('car');
      
      // Limpiar draft
      if (typeof window !== 'undefined') {
        localStorage.removeItem(DRAFT_KEY);
      }

      if (onOrderCreated && response?.order_id) {
        onOrderCreated(response.order_id);
      }

      // Disparar evento para actualizar changas board
      window.dispatchEvent(new CustomEvent('changas-board-refresh'));
    } catch (e) {
      console.error('[PromoBuyerActions] Exception:', e);
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, selectedItems, items, effectiveTransportationType, note.id, toast, onOrderCreated, calculateTotal, setTransportationType]);

  const total = calculateTotal();
  const hasSelection = Object.keys(selectedItems).length > 0;

  // Calcular costo del viaje basado en distancia
  const deliveryDistance = relevantLocation && note.latitude && note.longitude
    ? calculateDistance(
        note.latitude,
        note.longitude,
        relevantLocation.latitude,
        relevantLocation.longitude
      )
    : 0;

  // Tarifa base según tipo de transporte y costo por km
  // Usar `DELIVERY_PRICES` como fuente de verdad (servicio: paquete)
  const getDeliveryFee = () => {
    if (deliveryDistance <= 0) return 0;

    // Normalizar tipo de transporte al enum usado en la configuración
    let transportKey: string = 'auto';
    const t = effectiveTransportationType?.toString().toLowerCase() || '';
    if (['car', 'auto'].includes(t)) transportKey = 'auto';
    else if (['motorcycle', 'moto', 'bike', 'bicycle'].includes(t)) transportKey = 'moto';
    else if (t === 'van') transportKey = 'van';

    const priceConfig = getPriceConfig(transportKey, 'paquete');
    const fee = Math.round(priceConfig.base + deliveryDistance * priceConfig.perKm);
    return fee;
  };

  const baseDeliveryFee = getDeliveryFee();
  // Normalizar tipo de transporte y obtener priceConfig para mostrar cálculo consistente
  const normalizeTransportKey = (t: string) => {
    const tx = (t || '').toString().toLowerCase();
    if (['car', 'auto'].includes(tx)) return 'auto';
    if (['motorcycle', 'moto', 'bike', 'bicycle'].includes(tx)) return 'moto';
    if (tx === 'van') return 'van';
    return 'auto';
  };
  const currentTransportKey = normalizeTransportKey(effectiveTransportationType);
  const currentPriceConfig = getPriceConfig(currentTransportKey, 'paquete');
  
  // Renombrar para claridad: total es el SUBTOTAL (suma de items)
  const subtotal = total;  // Suma de precios de items seleccionados
  
  // Calcular comisiones basadas en SUBTOTAL
  const systemCommission = subtotal * COMMISSIONS.SYSTEM_COMMISSION_RATE;    // 1% del subtotal
  const hunterCommission = subtotal * COMMISSIONS.HUNTER_COMMISSION_RATE;    // 3% del subtotal (ajustado)

  // El costo de envío es flete + 1% del subtotal trasladado desde hunter (solo paquetería)
  const extraDeliveryFromSubtotal = Math.round(subtotal * 0.01);
  const deliveryFee = baseDeliveryFee + extraDeliveryFromSubtotal;  // Envío puro + 1%
  
  // Total final: subtotal + comisiones + envío (todo en PESOS)
  const finalTotal = subtotal + systemCommission + hunterCommission + deliveryFee;

  const handleOpenLocationDialog = () => {
    playBeep('add');
    vibrate('add');
    setIsChangingAddress(true);
  };

  const handleLocationSelect = (loc: NoteLocation | null) => {
    if (loc) {
      // Al seleccionar, abrimos refinamiento en lugar de guardar directo
      setTempCoords({ lat: loc.latitude, lon: loc.longitude });
      setRefineDetails({
        streetName: loc.road || '',
        altura: loc.houseNumber || '',
        floor: '', 
        apartment: '',
        notes: ''
      });
      setIsRefining(true);
      setIsTypingLocation(false);
    }
  };

  const handleConfirmRefinement = () => {
    if (!refineDetails.altura.trim()) {
      playBeep('decrease');
      vibrate('error');
      setShowAlturaError(true);
      toast({
        title: "Altura requerida",
        description: "Por favor ingresa la numeración de la calle.",
        variant: "destructive"
      });
      return;
    }

    if (tempCoords) {
      const street = refineDetails.streetName || '';
      const number = refineDetails.altura || '';
      const floorStr = refineDetails.floor ? ` Piso ${refineDetails.floor}` : '';
      const deptoStr = refineDetails.apartment ? ` Depto ${refineDetails.apartment}` : '';
      
      const finalDisplay = `${street} ${number}${floorStr}${deptoStr}`.trim();
      
      setRelevantLocation({
        displayName: finalDisplay,
        latitude: tempCoords.lat,
        longitude: tempCoords.lon,
        road: street,
        houseNumber: number
      });
      
      setIsRefining(false);
      setIsChangingAddress(false);
      playBeep('add');
      vibrate('success');
      toast({
        title: "Ubicación actualizada",
        description: "La dirección ha sido refinada y guardada.",
      });
    }
  };

  const handleUseCurrentLocation = async () => {
    setCalculatingLocation(true);
    try {
      if (!navigator.geolocation) {
        toast({
          title: 'Error',
          description: 'Geolocalización no disponible',
          variant: 'destructive',
        });
        setCalculatingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const p = position.coords;
            const r = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${p.latitude}&lon=${p.longitude}&zoom=18&addressdetails=1`,
              { mode: 'cors' }
            );
            if (!r.ok) throw new Error('reverse failed');
            const j = await r.json();
            const displayName = j.display_name || 'Mi ubicación actual';
            const address = j.address || {};
            
            handleLocationSelect({
              displayName,
              latitude: p.latitude,
              longitude: p.longitude,
              road: address.road || address.pedestrian || address.suburb || '',
              houseNumber: address.house_number || ''
            });
            setCalculatingLocation(false);
          } catch (e) {
            toast({
              title: 'Error',
              description: 'No se pudo obtener la dirección',
              variant: 'destructive',
            });
            setCalculatingLocation(false);
          }
        },
        (err) => {
          toast({
            title: 'Error',
            description: 'No se pudo obtener tu ubicación',
            variant: 'destructive',
          });
          setCalculatingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } catch (err) {
      setCalculatingLocation(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 min-w-0">
          <ShoppingCart className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">Conseguímelo por {note.store_name}</span>
        </h3>
        {Object.keys(selectedItems).length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground hover:text-destructive flex-shrink-0"
            onClick={() => {
              setSelectedItems({});
              setTransportationType('car');
              setRefineDetails({ streetName: '', altura: '', floor: '', apartment: '', notes: '' });
              if (typeof window !== 'undefined') {
                localStorage.removeItem(DRAFT_KEY);
              }
              playBeep('decrease');
              vibrate('decrease');
            }}
          >
            Limpiar
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Listado de items del promo */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Productos disponibles</Label>
          {items.length === 0 ? (
            <p className="text-sm text-gray-600 italic">No hay productos especificados en este promo</p>
          ) : (
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-card rounded-xl border border-border/50 p-3 flex gap-3 group hover:border-primary/30 transition-all shadow-sm hover:shadow-md"
                >
                  <div className="h-20 w-20 rounded-lg bg-primary/10 flex-shrink-0 relative overflow-hidden flex items-center justify-center">
                    <ShoppingCart className="w-10 h-10 text-primary/30" />
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground line-clamp-1" title={item.name}>{item.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
                        Disponible para ordenar desde esta promo
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-2 gap-2">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-bold text-primary">{formatCurrency(item.price)}</span>
                        {selectedItems[idx] > 0 && (
                          <span className="inline-flex items-center gap-1 w-fit px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold leading-none">
                            <ShoppingCart className="w-2.5 h-2.5 shrink-0" />
                            {selectedItems[idx]} en pedido
                          </span>
                        )}
                      </div>

                      <div className="h-[43px] flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                        {selectedItems[idx] > 0 ? (
                          <QuantitySelector
                            cartItemId={`promo-item-${idx}`}
                            quantity={selectedItems[idx] || 1}
                            onUpdateQuantity={(_, quantityCalculator) => {
                              handleQuantityChange(idx, quantityCalculator(selectedItems[idx] || 0));
                            }}
                            onPlayBeep={playBeep}
                            onVibrate={vibrate}
                            disabled={loading}
                          />
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              playBeep('add');
                              vibrate('add');
                              handleQuantityChange(idx, 1);
                            }}
                            disabled={loading}
                            className="h-[43px] w-[43px] p-0 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all active:scale-75 duration-75 flex items-center justify-center shrink-0 aspect-square disabled:opacity-50 disabled:cursor-wait"
                            title="Agregar al pedido"
                          >
                            <Plus className="h-6 w-6" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total y botón */}
        {hasSelection && (
          <div className="space-y-3">
            <div className="p-3 bg-card rounded-md border border-green-200">
              {isChangingAddress ? (
                <div className="space-y-4">
                  {/* Header de Edición */}
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] text-muted-foreground uppercase font-bold leading-tight flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {isRefining ? 'Refinar mi ubicación' : 'Cambiar dirección'}
                    </p>
                    {!isRefining && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-5 w-5" 
                        onClick={() => {
                          setIsChangingAddress(false);
                          setIsTypingLocation(false);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  
                  {isRefining ? (
                    /* FORM DE REFINAR (Estética RequestRide) */
                    <motion.div
                      key={
                        isRefining +
                        '-' +
                        [
                          refineDetails.streetName,
                          refineDetails.altura,
                          refineDetails.floor,
                          refineDetails.apartment,
                          refineDetails.notes
                        ].join('-')
                      }
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-4 overflow-hidden"
                    >
                      <div className="rounded-lg overflow-hidden border" style={{ height: 160 }}>
                        <MapPinSelector
                          lat={tempCoords?.lat || relevantLocation?.latitude || 0}
                          lon={tempCoords?.lon || relevantLocation?.longitude || 0}
                          onChange={(lat, lon) => setTempCoords({ lat, lon })}
                          height="160px"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="min-w-0">
                          <Label className="text-[10px] uppercase font-bold">Calle</Label>
                          <Input
                            value={refineDetails.streetName}
                            onChange={(e) => setRefineDetails(prev => ({ ...prev, streetName: e.target.value }))}
                            placeholder="Calle"
                            className="h-8 text-xs w-full"
                          />
                        </div>
                        <div className="min-w-0">
                          <Label className={`text-[10px] uppercase font-bold ${showAlturaError ? 'text-primary' : ''}`}>Altura *</Label>
                          <Input
                            value={refineDetails.altura}
                            onChange={(e) => {
                              setRefineDetails(prev => ({ ...prev, altura: e.target.value }));
                              if (e.target.value.trim()) setShowAlturaError(false);
                            }}
                            placeholder="Nro"
                            inputMode="numeric"
                            className={`h-8 text-xs w-full ${showAlturaError ? 'border-primary border-2' : ''}`}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="min-w-0">
                          <Label className="text-[10px] uppercase font-bold">Piso</Label>
                          <Input
                            value={refineDetails.floor}
                            onChange={(e) => setRefineDetails(prev => ({ ...prev, floor: e.target.value }))}
                            className="h-8 text-xs w-full"
                          />
                        </div>
                        <div className="min-w-0">
                          <Label className="text-[10px] uppercase font-bold">Depto</Label>
                          <Input
                            value={refineDetails.apartment}
                            onChange={(e) => setRefineDetails(prev => ({ ...prev, apartment: e.target.value }))}
                            className="h-8 text-xs w-full"
                          />
                        </div>
                      </div>

                      <div className="min-w-0">
                        <Label className="text-[10px] uppercase font-bold">Notas de entrega</Label>
                        <Textarea
                          value={refineDetails.notes}
                          onChange={(e) => setRefineDetails(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Ej: Portón blanco..."
                          className="h-16 text-xs w-full resize-none"
                        />
                      </div>

                      <div className="flex gap-2 w-full">
                        <Button
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => setIsRefining(false)}
                        >
                          Atrás
                        </Button>
                        <Button
                          className="flex-1 h-8 text-xs font-bold"
                          onClick={handleConfirmRefinement}
                        >
                          Confirmar
                        </Button>
                      </div>
                    </motion.div>
                  ) : (
                    /* LISTA DE DIRECCIONES / AUTOCOMPLETE */
                    <div className="space-y-4 overflow-hidden">
                      <div className="w-full overflow-hidden">
                        <LocationAutocomplete
                          value={null}
                          onChange={handleLocationSelect}
                          onInputChange={(val) => setIsTypingLocation(val.length > 0)}
                          placeholder="Busca tu dirección..."
                          showGps={false}
                        />
                      </div>

                      {!isTypingLocation && (
                        <div className="space-y-2 overflow-hidden">
                          <button
                            type="button"
                            className="flex items-center border rounded p-3 min-h-[50px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2 text-left overflow-hidden"
                            onClick={handleUseCurrentLocation}
                            disabled={calculatingLocation}
                          >
                            <LocateFixed className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="font-bold text-xs truncate">
                              {calculatingLocation ? 'Obteniendo ubicación...' : 'Tu ubicación actual'}
                            </span>
                          </button>

                          <AnimatePresence>
                            {addresses.length > 0 && (
                              <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1 overflow-x-hidden">
                                {addresses.map((addr, index) => (
                                  <motion.div
                                    key={addr.id}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="flex items-center justify-between border rounded p-3 min-h-[50px] cursor-pointer hover:bg-muted transition-colors text-left gap-2 overflow-hidden"
                                    onClick={() => {
                                      // Al seleccionar una guardada, podemos tomar sus componentes si los tiene
                                      setTempCoords({ lat: addr.location.latitude, lon: addr.location.longitude });
                                      setRefineDetails(prev => ({
                                        ...prev,
                                        streetName: addr.streetName || '',
                                        altura: addr.altura || '',
                                        floor: addr.floor || '',
                                        apartment: addr.apartment || '',
                                        notes: addr.notes || ''
                                      }));
                                      setTimeout(() => setIsRefining(true), 0);
                                    }}
                                  >
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                      <div className="font-bold text-xs truncate">{addr.label || addr.location.displayName}</div>
                                      {(addr.floor || addr.apartment) && (
                                        <div className="text-[10px] text-muted-foreground truncate">
                                          {[addr.floor ? `Piso ${addr.floor}` : null, addr.apartment ? `Depto ${addr.apartment}` : null].filter(Boolean).join(' · ')}
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <p className="text-[10px] text-muted-foreground italic">
                    La tarifa de envío se recalculará automáticamente
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <MapPin className="h-4 w-4 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold leading-tight">Entregar en</p>
                        <p className="text-xs font-bold leading-tight break-words line-clamp-2">
                          {relevantLocation?.displayName || 'Ubicación no seteada'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 px-2 text-[10px] font-bold uppercase text-primary hover:text-primary hover:bg-primary/10 shrink-0"
                      onClick={handleOpenLocationDialog}
                    >
                      Cambiar
                    </Button>
                  </div>

                  {!hideTransportSelector && (
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase font-bold leading-tight mb-2 block">
                        Tipo de transporte
                      </Label>
                      <div className="grid grid-cols-4 gap-2 mb-1">
                        {[
                          { value: 'car', label: 'Auto', Icon: Car },
                          { value: 'motorcycle', label: 'Moto', Icon: Motorbike },
                          { value: 'bike', label: 'Bici', Icon: Bike },
                          { value: 'van', label: 'Van', Icon: Truck },
                        ].map(({ value, label, Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setTransportationType(value)}
                            disabled={loading}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                              effectiveTransportationType === value
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                            onMouseDown={() => {
                              playBeep('add');
                              vibrate('add');
                            }}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="text-xs font-semibold">{label}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        El driver debe tener un vehículo apto para esta cantidad de productos
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-3 bg-card rounded-md border border-primary/20 space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-medium">Productos: </span>
                <span className="text-sm font-bold text-foreground">{formatCurrency(subtotal)}</span>
              </div>
              
              {/* Costo de envío con tooltip simple */}
              <div className="flex justify-between items-baseline relative">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Costo de envío:</span>
                  <button 
                    type="button" 
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-primary/10 transition-colors"
                    onMouseEnter={() => setShowCostTooltip(true)}
                    onMouseLeave={() => setShowCostTooltip(false)}
                  >
                    <Info className="h-4 w-4 text-primary" />
                  </button>
                  
                  {/* Tooltip simple */}
                  {showCostTooltip && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-3 bg-secondary border border-border rounded shadow-2xl z-[9999] pointer-events-none whitespace-nowrap">
                      <div className="space-y-1.5 min-w-[240px]">
                        {deliveryDistance > 0 && (
                          <div className="flex flex-col gap-1 text-[11px]">
                            <div className="flex justify-between gap-4">
                              <span className="font-medium">Flete ({deliveryDistance.toFixed(2)} km):</span>
                              <span className="text-foreground">{formatCurrency(deliveryFee)}</span>
                            </div>
                            <div className="flex flex-col gap-1 text-[11px] text-muted-foreground">
                              <div className="flex justify-between">
                                <span className="">Distancia:</span>
                                <span className="">{`${currentPriceConfig.base} + ${currentPriceConfig.perKm} × ${deliveryDistance.toFixed(2)} = ${formatCurrency(Math.round(currentPriceConfig.base + deliveryDistance * currentPriceConfig.perKm))}`}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="">Proporcionalidad de envío (1%):</span>
                                <span className="">{formatCurrency(extraDeliveryFromSubtotal)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between gap-4 text-[11px]">
                          <span className="font-medium">Comisión Sistema ({Math.round(COMMISSIONS.SYSTEM_COMMISSION_RATE * 100)}%):</span>
                          <span className="text-foreground">{formatCurrency(systemCommission)}</span>
                        </div>
                        <div className="flex justify-between gap-4 text-[11px]">
                          <span className="font-medium">Comisión Cazador ({Math.round(COMMISSIONS.HUNTER_COMMISSION_RATE * 100)}%):</span>
                          <span className="text-foreground">{formatCurrency(hunterCommission)}</span>
                        </div>
                        <div className="pt-1.5 border-t border-border/50 flex justify-between gap-4 text-xs font-bold">
                          <span>Total cargos:</span>
                          <span className="text-foreground">{formatCurrency(deliveryFee + systemCommission + hunterCommission)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-sm font-bold text-foreground">{formatCurrency(deliveryFee + systemCommission + hunterCommission)}</span>
              </div>

              <div className="flex justify-between items-baseline border-t border-border pt-2">
                <span className="text-sm font-bold">Total: </span>
                <span className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(finalTotal)}</span>
              </div>

              <p className="text-[10px] text-muted-foreground mt-2 leading-tight">
                El total incluye productos, comisiones y tarifa de envío.
              </p>
              
              <Button
                onClick={() => {
                  playBeep('add');
                  vibrate('success');
                  void handleConsiguemelo();
                }}
                disabled={loading || !hasSelection}
                className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 font-bold mt-2"
                size="lg"
              >
                {loading ? 'Creando orden...' : `Conseguímelo - ${formatCurrency(finalTotal)}`}
              </Button>
            </div>
          </div>
        )}

        {!hasSelection && (
          <div className="p-3 bg-muted rounded-md text-center">
            <p className="text-sm text-muted-foreground">Selecciona productos para continuar</p>
          </div>
        )}
      </div>
    </div>
  );
}
